import { Loading, Pagination } from '@wfp/react';
import React, { Fragment, useEffect } from 'react';
import {
  useFilters,
  useFlexLayout,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table';

// react-table prop getters include a `key`, which React 19 rejects inside a
// spread; the elements below set their own key explicitly.
const withoutKey = <T extends { key?: React.Key }>({
  key: _key,
  ...props
}: T) => props;

interface Props {
  className?: string;
  data: any[];
  columns: any[];
  loading?: boolean;
  pagination?: string;
  isFilterable?: boolean;
  initialState?: any;
  totalItems?: number;
  fetchData?: (opts: { pageIndex: number; pageSize: number }) => void;
  pageCount?: number;
  currentPage?: number;
}

const TableMain = ({
  className = '',
  data,
  columns,
  loading = false,
  pagination = '',
  isFilterable = false,
  initialState = {
    hiddenColumns: ['hq-admin-structure-column'],
  },
  totalItems,
  fetchData,
  pageCount = -1,
  currentPage = 1,
}: Props) => {
  const isServerPaginated = pagination === 'server';

  const defaultColumn = {
    minWidth: 30,
    width: 50,
    maxWidth: 400,
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
    setPageSize,
    rows,
    pageCount: clientPageCount,
    state: { pageIndex: pageIndexState, pageSize: pageSizeState },
  } = (useTable as any)(
    {
      columns,
      data,
      defaultColumn,
      initialState,
      manualPagination: isServerPaginated,
      autoResetPage: false,
      pageCount: isServerPaginated ? pageCount : -1,
    },
    useFlexLayout,
    useResizeColumns,
    useFilters,
    useSortBy,
    usePagination,
  );

  function onPaginationChange({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) {
    setPageSize(pageSize);
    gotoPage(isServerPaginated ? page : page - 1);
  }

  useEffect(() => {
    if (!pagination) {
      setPageSize(data?.length || 1);
    }
  }, [data?.length]);

  useEffect(() => {
    if (isServerPaginated) {
      fetchData({ pageIndex: pageIndexState, pageSize: pageSizeState });
    }
  }, [fetchData, pageIndexState, pageSizeState]);

  return (
    <div className={`${className ? className : ''} ReactTable`}>
      {loading && <Loading withOverlay={true} active />}
      <div {...getTableProps()} className="rt-table">
        <div className="rt-thead -header">
          {headerGroups.map(headerGroup => (
            <div
              {...withoutKey(headerGroup.getHeaderGroupProps())}
              key={`${headerGroup.headers
                .map(column => column.id)
                .join('-')}-header-group`}
              className="rt-tr"
            >
              {headerGroup.headers.map(column => {
                let sortClass = '';
                if (column.isSorted) {
                  sortClass = column.isSortedDesc ? '-sort-desc' : '-sort-asc';
                }
                return (
                  <div
                    {...withoutKey(
                      column.getHeaderProps(column.getSortByToggleProps()),
                    )}
                    className={`rt-th rt-resizable-header ${sortClass}`}
                    key={`${column.id}-header`}
                  >
                    <div
                      className="rt-resizable-header-content"
                      data-test-id="column-title"
                    >
                      {column.render('Header')}
                    </div>
                    <div {...column.getResizerProps()} className="rt-resizer" />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {isFilterable && (
          <div className="rt-thead -filters">
            {headerGroups.map(headerGroup => (
              <div
                key={`${headerGroup.headers
                  .map(column => column.id)
                  .join('-')}-filters-group`}
                className="rt-tr"
              >
                {headerGroup.headers.map(column => {
                  return (
                    <div
                      {...withoutKey(column.getHeaderProps())}
                      className="rt-th"
                      key={`${column.id}-filter`}
                    >
                      {column.canFilter ? column.render('Filter') : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        <div className="rt-tbody">
          {totalItems === 0 && !loading && (
            <div className="table-main-empty-results">No items found</div>
          )}
          <div {...getTableBodyProps()} className="rt-tr-group">
            {page.map((row, ind) => {
              prepareRow(row);
              return (
                <Fragment key={row.id}>
                  <div
                    {...withoutKey(row.getRowProps())}
                    className={`rt-tr ${ind % 2 === 0 ? '-odd' : '-even'}`}
                  >
                    {row.cells.map(cell => {
                      return (
                        <div
                          {...withoutKey(cell.getCellProps())}
                          key={`${cell.column.id}-cell`}
                          className={`rt-td ${cell.column.className}`}
                        >
                          {cell.render('Cell')}
                        </div>
                      );
                    })}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {pagination && (
        <Pagination
          page={currentPage}
          onChange={onPaginationChange}
          pageSizes={[10, 20, 30, 40, 50]}
          totalItems={isServerPaginated ? totalItems : rows?.length}
          data-test-id="pagination"
          {...{ pages: isServerPaginated ? pageCount : clientPageCount }}
        />
      )}
    </div>
  );
};

export default TableMain;
